import React, { useState } from 'react'

import { EuiFlyoutHeader, EuiFlyoutBody, EuiFlyoutFooter, EuiTitle, EuiButton, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiFieldText, EuiTextArea, EuiSpacer } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { CompetitorProfile } from 'api/interfaces/MarketAnalysis'
import BlueprintEditWarning from 'features/planner/components/BlueprintEditWarning'

interface EditCompetitorProfileFlyoutProps {
  mode: 'add' | 'edit'
  profile?: CompetitorProfile
  onSave: (profile: CompetitorProfile) => void
  onClose: () => void
  isUpdating: boolean
  showBlueprintWarning?: boolean
}

const emptyProfile: CompetitorProfile = {
  name: '',
  strategicThreatLevel: '',
  overview: '',
  strengths: '',
  weaknesses: '',
  strategicImplications: ''
}

const EditCompetitorProfileFlyout: React.FC<EditCompetitorProfileFlyoutProps> = ({ mode, profile, onSave, onClose, isUpdating, showBlueprintWarning = false }) => {
  const [formData, setFormData] = useState<CompetitorProfile>(profile ?? emptyProfile)

  const handleSave = () => {
    onSave(formData)
  }

  const isSaveDisabled = !formData.name.trim()

  return (
    <AcFlyout onClose={onClose} size="l">
      <EuiFlyoutHeader>
        <EuiTitle size="m">
          <h2>{mode === 'add' ? 'Add Competitor Profile' : `Edit ${formData.name}`}</h2>
        </EuiTitle>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <BlueprintEditWarning show={showBlueprintWarning} />
        <EuiFormRow label="Name" fullWidth>
          <EuiFieldText
            fullWidth
            value={formData.name}
            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
          />
        </EuiFormRow>
        <EuiSpacer size="m" />
        <EuiFormRow label="Strategic Threat Level" fullWidth>
          <EuiFieldText
            fullWidth
            value={formData.strategicThreatLevel}
            onChange={e => setFormData(prev => ({ ...prev, strategicThreatLevel: e.target.value }))}
          />
        </EuiFormRow>
        <EuiSpacer size="m" />
        <EuiFormRow label="Overview" fullWidth>
          <EuiTextArea
            fullWidth
            value={formData.overview}
            onChange={e => setFormData(prev => ({ ...prev, overview: e.target.value }))}
            rows={4}
          />
        </EuiFormRow>
        <EuiSpacer size="m" />
        <EuiFormRow label="Strengths" fullWidth>
          <EuiTextArea
            fullWidth
            value={formData.strengths}
            onChange={e => setFormData(prev => ({ ...prev, strengths: e.target.value }))}
            rows={4}
          />
        </EuiFormRow>
        <EuiSpacer size="m" />
        <EuiFormRow label="Weaknesses" fullWidth>
          <EuiTextArea
            fullWidth
            value={formData.weaknesses}
            onChange={e => setFormData(prev => ({ ...prev, weaknesses: e.target.value }))}
            rows={4}
          />
        </EuiFormRow>
        <EuiSpacer size="m" />
        <EuiFormRow label="Strategic Implications" fullWidth>
          <EuiTextArea
            fullWidth
            value={formData.strategicImplications}
            onChange={e => setFormData(prev => ({ ...prev, strategicImplications: e.target.value }))}
            rows={4}
          />
        </EuiFormRow>
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        <EuiFlexGroup direction="row" alignItems="center">
          <EuiFlexItem grow={false}>
            <EuiButton onClick={onClose} isDisabled={isUpdating}>
              Cancel
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton fill onClick={handleSave} isLoading={isUpdating} isDisabled={isSaveDisabled}>
              Save
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default EditCompetitorProfileFlyout
